import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ContentCard from "@/components/base/ContentCard";
import EmptyState from "@/components/base/EmptyState";
import { FilterChipGroup, SortTabs } from "@/components/base/Filters";
import PageIntro from "@/components/base/PageIntro";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { movieItems, newsItems } from "@/mocks/content";
import { formatViews } from "@/lib/format";

const PAGE_SIZE = 4;

const dateOptions = [
  { value: "all", label: "全部时间" },
  { value: "week", label: "近一周" },
  { value: "month", label: "近一月" },
];

const typeOptions = [
  { value: "电影新闻", label: "电影新闻" },
  { value: "电视剧新闻", label: "电视剧新闻" },
  { value: "明星动态", label: "明星动态" },
];

const sortOptions = [
  { value: "latest", label: "最新发布" },
  { value: "hot", label: "按热门度" },
];

export default function NewsListPage() {
  usePageMeta(
    "娱乐资讯 - 红桃网",
    "红桃网娱乐资讯频道，聚焦电影新闻、电视剧动态与明星动态，按时间与热度整理每日娱乐热点，帮你快速掌握行业变化。",
    "娱乐资讯,电影新闻,电视剧新闻,明星动态,红桃网",
  );

  const [dateFilter, setDateFilter] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = newsItems.filter((item) => {
      const dateOk =
        dateFilter === "all" ||
        (dateFilter === "week" && item.timeRange === "week") ||
        (dateFilter === "month" && item.timeRange !== "older");
      const typeOk = selectedTypes.length === 0 || selectedTypes.includes(item.newsType);
      return dateOk && typeOk;
    });

    return list.sort((a, b) => {
      if (sort === "hot") return b.views - a.views;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
  }, [dateFilter, selectedTypes, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const toggleType = (value: string) => {
    setSelectedTypes((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
    setPage(1);
  };

  const resetFilters = () => {
    setDateFilter("all");
    setSelectedTypes([]);
    setSort("latest");
    setPage(1);
  };

  const activeFilterCount = (dateFilter !== "all" ? 1 : 0) + selectedTypes.length;
  const hotNews = [...newsItems].sort((a, b) => b.views - a.views).slice(0, 7);
  const sidebarMovies = movieItems.filter((movie) => movie.recommendIndex >= 90).slice(0, 3);

  return (
    <>
      <PageIntro
        kicker="资讯频道"
        title="娱乐资讯"
        description="电影新闻、电视剧动态与明星现场，我们只挑值得知道的部分，并尽量把来龙去脉讲清楚。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "娱乐资讯" }]}
        extra={
          <div className="flex items-center gap-5 text-sm">
            <div>
              <p className="font-label text-xl font-extrabold text-foreground-950">{newsItems.length}</p>
              <p className="text-xs text-foreground-500">篇在库资讯</p>
            </div>
            <span className="h-8 w-px bg-background-300" />
            <div>
              <p className="font-label text-xl font-extrabold text-primary-600">3</p>
              <p className="text-xs text-foreground-500">个新闻类型</p>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-portal px-4 py-6 md:px-6 md:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between gap-3 border-b border-background-200 px-4 py-3">
                <div className="flex items-center gap-2">
                  <i className="ri-filter-3-line text-primary-500" />
                  <span className="font-heading text-sm font-semibold text-foreground-900">筛选条件</span>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-primary-500 px-1.5 py-0.5 font-label text-[10px] text-background-50">
                      {activeFilterCount}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  className="flex cursor-pointer items-center gap-1 text-xs text-foreground-500 md:hidden"
                >
                  {filtersOpen ? "收起" : "展开"}
                  <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                </button>
              </div>

              <div className={`${filtersOpen ? "block" : "hidden"} space-y-3 px-4 py-4 md:block`}>
                <FilterChipGroup
                  label="发布时间"
                  options={dateOptions}
                  selected={[dateFilter]}
                  onSelect={(value) => {
                    setDateFilter(value);
                    setPage(1);
                  }}
                />
                <FilterChipGroup
                  label="新闻类型"
                  options={typeOptions}
                  selected={selectedTypes}
                  onSelect={toggleType}
                />
                <p className="pl-0 text-[11px] text-foreground-400 sm:pl-[72px]">新闻类型支持多选，结果实时刷新。</p>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 px-4 py-3">
                <p className="text-xs text-foreground-500">
                  共 <span className="font-label font-semibold text-foreground-900">{filtered.length}</span> 条结果
                  {filtered.length > 0 && (
                    <span className="ml-1 hidden sm:inline">· 第 {currentPage} / {totalPages} 页</span>
                  )}
                </p>
                <SortTabs
                  options={sortOptions}
                  value={sort}
                  onChange={(value) => {
                    setSort(value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {pageItems.length > 0 ? (
                pageItems.map((item) => <ContentCard key={item.id} item={item} variant="row" showSummaryAlways />)
              ) : (
                <EmptyState
                  title="没有符合条件的内容"
                  description="试着放宽时间范围，或减少新闻类型的筛选条件，也可以直接重置全部筛选。"
                  actionLabel="重置筛选"
                  onAction={resetFilters}
                />
              )}
            </div>

            {filtered.length > 0 && (
              <div className="mt-6">
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </div>
            )}
          </div>

          <aside className="space-y-4 self-start">
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  资讯热榜
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">热榜</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {hotNews.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                      <span
                        className={[
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold",
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500",
                        ].join(" ")}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug text-foreground-800 transition-colors group-hover:text-primary-600 line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 flex items-center gap-1 text-[11px] text-foreground-400">
                          <i className="ri-eye-line" />
                          {formatViews(item.views)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-film-line text-primary-500" />
                相关影视推荐
              </h2>
              <ul className="mt-3 space-y-3">
                {sidebarMovies.map((movie) => (
                  <li key={movie.id}>
                    <Link to={`/movies/${movie.id}`} className="group flex cursor-pointer gap-3">
                      <span className="h-16 w-12 shrink-0 overflow-hidden rounded-sm bg-background-200">
                        <img
                          src={movie.cover}
                          alt={`${movie.title} 海报`}
                          title={`${movie.title} - 红桃网影视推荐`}
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                          {movie.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-400">
                          {movie.year} · {movie.region} · {movie.genres.join(" / ")}
                        </span>
                        <span className="mt-1 flex items-center gap-1 font-label text-[11px] text-accent-700">
                          <i className="ri-star-fill text-[10px]" />
                          {movie.rating.toFixed(1)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-4">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本页所有资讯均为虚构演示内容，浏览量数据仅用于展示排序与筛选效果。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}